import { useState } from 'react'
import { Plus, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ASSISTANT_MAX_CONSTRAINTS } from '@sdr-agend/admin-schemas'

interface AssistantConstraintsStepProps {
  constraints: string[]
  onChange: (next: string[]) => void
  disabled?: boolean
}

/**
 * Story 3.18 §AC #7 — Step 2: Constraints.
 *
 * Allows the operator to add free-text rules ("o que evitar") as chips. Each
 * chip carries an "X" button to remove it. The list is capped at
 * ASSISTANT_MAX_CONSTRAINTS (20) to match the backend schema.
 */
export function AssistantConstraintsStep({
  constraints,
  onChange,
  disabled = false,
}: AssistantConstraintsStepProps): React.ReactElement {
  const [draft, setDraft] = useState('')
  const atCap = constraints.length >= ASSISTANT_MAX_CONSTRAINTS

  function add(): void {
    const trimmed = draft.trim()
    if (trimmed.length === 0 || atCap) return
    onChange([...constraints, trimmed])
    setDraft('')
  }

  function remove(idx: number): void {
    onChange(constraints.filter((_, i) => i !== idx))
  }

  return (
    <div className="grid gap-4">
      <div className="grid gap-2">
        <Label htmlFor="assistant-constraint-input">
          Restrição (adicione uma por vez)
        </Label>
        <div className="flex gap-2">
          <Input
            id="assistant-constraint-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                add()
              }
            }}
            disabled={disabled || atCap}
            placeholder="Ex: nunca prometer reembolso"
            maxLength={500}
            aria-describedby="assistant-constraints-help"
          />
          <Button
            type="button"
            variant="outline"
            onClick={add}
            disabled={disabled || atCap || draft.trim().length === 0}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            Adicionar
          </Button>
        </div>
        <p id="assistant-constraints-help" className="text-xs text-muted-foreground">
          {constraints.length} / {ASSISTANT_MAX_CONSTRAINTS} restrições · pressione Enter para
          adicionar.
        </p>
      </div>

      <ul aria-label="Lista de restrições adicionadas" className="flex flex-wrap gap-2">
        {constraints.map((c, idx) => (
          <li
            key={`${idx}-${c}`}
            className="flex items-center gap-2 rounded-full border bg-muted/30 px-3 py-1 text-xs"
          >
            <span>{c}</span>
            <button
              type="button"
              aria-label={`Remover restrição: ${c}`}
              onClick={() => remove(idx)}
              disabled={disabled}
              className="rounded-full p-0.5 hover:bg-destructive/10 hover:text-destructive"
            >
              <X className="h-3 w-3" aria-hidden="true" />
            </button>
          </li>
        ))}
        {constraints.length === 0 && (
          <li className="text-xs text-muted-foreground">Nenhuma restrição adicionada.</li>
        )}
      </ul>
    </div>
  )
}
